import { ChevronDown, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { HelpTip } from './HelpTip';
import { useRateComparison } from '../lib/useSettings';
import {
  carriedRows,
  countsLine,
  emptyMessage,
  filterRows,
  formatCell,
  jobsFilterLabel,
  keyCellLines,
  ratesFor,
  visibleJobs,
} from '../lib/rateComparison';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import type { RateComparisonJob, RateComparisonKind } from '@coffman-tm/shared';

/**
 * Compare rates — the same person or the same machine, across every job the
 * PM is on, in one table.
 *
 * READ-ONLY, deliberately. Rates are per job (client ruling 2026-09-21: two
 * jobs are not one contract), so a rate is changed on that job's own roster
 * where whoever changes it is looking at that job's paperwork. This screen
 * exists because the alternative was opening four jobs in four tabs to answer
 * "what do we carry him at over there" — so it answers that, and stops. No
 * editing, no per-row action, and no highlighting: a difference between two
 * jobs is frequently correct, so coloring one would be the portal claiming a
 * finding it cannot actually make. The reader decides; the controls just
 * narrow what they read.
 *
 * One rate per job, and that is the day rate. A swing column shipped and came
 * straight back out (2026-09-21): swing is too small a share of what these
 * jobs bill to earn a control on this screen. The API still sends a swing rate
 * per job, so putting the column back is a web-side change if Coffman asks.
 *
 * Mounted only while open (the caller renders it conditionally), which is what
 * makes the fetch lazy — the Settings page must not spend a cross-job read on
 * every visit to a roster tab.
 */
export function RateComparisonModal({
  kind,
  onClose,
}: {
  kind: RateComparisonKind;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useRateComparison(kind);
  const [query, setQuery] = useState('');
  // Off by default: the PM opens this to read the roster, not to be handed a
  // filtered subset of it before they know what is on screen.
  const [onlyDifferences, setOnlyDifferences] = useState(false);
  // Every job ticked on open — the table's job is to show what the jobs carry,
  // and a column missing because of a box the reader never touched would read
  // as a job they are not on. Nothing here is persisted (per open, by design):
  // hiding a column is how you read this table today, not a setting.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set<string>());
  // Clearing the search unmounts the button that did it, so focus has to be
  // handed somewhere deliberately — back to the box it just emptied.
  const searchRef = useRef<HTMLInputElement>(null);
  const clearSearch = () => {
    setQuery('');
    searchRef.current?.focus();
  };

  useBodyScrollLock(true);

  // Esc closes. Nothing here is unsaved, so there is nothing to confirm.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isEmployees = kind === 'employees';
  const title = `Compare rates — ${isEmployees ? 'Employees' : 'Equipment'}`;
  const jobs = data?.jobs ?? [];
  const rows = data?.rows ?? [];
  const shownJobs = visibleJobs(jobs, hidden);
  const jobIds = shownJobs.map((job) => job.project_id);
  // One ticked job has nothing to disagree with, so the toggle unmounts
  // (RULES.md 7.7) — and its remembered value must stop filtering with it,
  // or unticking down to one job would empty a table whose control is gone.
  const canCompare = shownJobs.length > 1;
  const differencesOnly = onlyDifferences && canCompare;
  // What the ticked jobs carry, before the search and the toggle narrow it —
  // the denominator the counts line has to use, or "175 employees" would be
  // counting rosters the reader unticked.
  const carried = carriedRows(rows, jobIds);
  const visible = filterRows(rows, { query, onlyDifferences: differencesOnly, jobIds });
  // A failed REFETCH must not take a good table off the screen — useApiQuery
  // refreshes on tab focus, and the rates on screen are still the rates that
  // loaded. Only a first load with nothing to show becomes the error state.
  const failedOutright = error !== null && data === null;
  const failedRefresh = error !== null && data !== null;

  const setJobShown = (projectId: string, shown: boolean) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (shown) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  // Whether any money column is off the right edge right now. It has to be
  // MEASURED: the answer depends on the modal's width, how many jobs are
  // ticked, whether the row count put a vertical scrollbar in the gutter, and
  // where the reader has already scrolled to — none of which the render knows.
  // Horizontal overflow is otherwise silent (a clipped column just looks
  // narrow), which is how eight columns came to read as all of them.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<Overflow>(NO_OVERFLOW);
  const measureOverflow = useCallback(() => {
    const el = scrollRef.current;
    const next: Overflow = el
      ? {
          moreRight: el.scrollWidth - el.clientWidth - el.scrollLeft > 1,
          // Inset by the REAL scrollbars rather than by a guessed 15px: the
          // width is the platform's, and either bar can be absent.
          gutterRight: el.offsetWidth - el.clientWidth,
          gutterBottom: el.offsetHeight - el.clientHeight,
        }
      : NO_OVERFLOW;
    setOverflow((prev) => (sameOverflow(prev, next) ? prev : next));
  }, []);
  // After every render, deliberately: the job picker, the search and the
  // differences toggle all change the table's width or its height, and a
  // dependency list naming them would be a list to forget to update. Setting
  // the same boolean is a no-op in React, so this cannot loop.
  useLayoutEffect(measureOverflow);
  useEffect(() => {
    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [measureOverflow]);

  return (
    <div
      className="pdf-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="new-pco-modal new-pco-modal--wide"
        onClick={(e) => e.stopPropagation()}
        // One width, the same on every open, for everybody — see
        // MODAL_MAX_WIDTH. Not derived from the jobs the reader holds and
        // certainly not from the ones they have ticked: the window does not
        // move while you read (client ruling 2026-09-21).
        //
        // Height is the same rule, and for the same reason: a search that
        // matched one row collapsed the window to a few hundred pixels,
        // because `.new-pco-modal`'s 85vh is a CAP and a short body simply
        // did not reach it. Stating it as the height pins the window at what
        // the full roster already reached, so the frame stays put and its
        // rows sit at the top of it with empty frame below — a table in a
        // frame, not a window that shrink-wraps whatever survived the search.
        // The cap stays too: the two agree, and one of them is the
        // app-wide convention for every modal.
        style={{ maxWidth: MODAL_MAX_WIDTH, height: '85vh' }}
      >
        <div className="pdf-modal-head">
          <div
            className="pdf-modal-title"
            style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'visible' }}
          >
            {title}
            {/* The one fact this table needs stated and cannot state itself,
                in a tip rather than a standing line under the title. */}
            <HelpTip title="What this table is" learnMore="settings">
              Every job you can open, side by side. Nothing here changes a rate — a rate is
              changed on that job’s own {isEmployees ? 'Employees' : 'Equipment'} list. A
              dash means the job doesn’t carry that {isEmployees ? 'person' : 'asset'} at
              all.
            </HelpTip>
          </div>
          <button type="button" className="pdf-modal-close" onClick={onClose} aria-label="Close">
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        {/* The BODY does not scroll — the table does (its own container, so the
            sticky header sticks to something). A second scroller here would put
            a scrollbar inside a scrollbar. */}
        <div style={bodyStyle}>
          <div style={controlsStyle}>
            <div style={searchWrapStyle}>
              <input
                ref={searchRef}
                type="text"
                style={searchStyle}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Escape empties the box, and only then. With nothing typed
                  // it is left to bubble to the modal's own handler, so the
                  // key that closes this window keeps closing it.
                  if (e.key === 'Escape' && query !== '') {
                    e.stopPropagation();
                    clearSearch();
                  }
                }}
                placeholder={isEmployees ? 'Search roster…' : 'Search assets…'}
                aria-label={
                  isEmployees ? 'Search people by name or ID' : 'Search assets by ID or description'
                }
              />
              {/* Same ✕-in-the-box idiom as the rail's PCO search and the help
                  search, on the shared .search-clear class. Rendered only with
                  something to clear (RULES.md 7.7). */}
              {query !== '' && (
                <button
                  type="button"
                  className="search-clear"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <X size={13} strokeWidth={2} aria-hidden />
                </button>
              )}
            </div>
            {/* Which jobs are columns. One job has no columns to choose
                between, so the picker doesn't render there at all. */}
            {jobs.length > 1 && (
              <JobsFilter
                jobs={jobs}
                hidden={hidden}
                shownCount={shownJobs.length}
                onToggle={setJobShown}
                onSelectAll={() => setHidden(new Set<string>())}
              />
            )}
            {/* One column compares with nothing, so the control doesn't
                render at all rather than sit there disabled (RULES.md 7.7). */}
            {canCompare && (
              <label style={toggleLabelStyle}>
                <input
                  type="checkbox"
                  checked={onlyDifferences}
                  onChange={(e) => setOnlyDifferences(e.target.checked)}
                />
                <span>Only show differences</span>
              </label>
            )}
          </div>

          {failedOutright ? (
            <p style={{ color: 'var(--danger)', fontSize: 13 }}>
              Couldn’t load the other jobs’ rates. Try again, and contact support if it keeps
              happening.
            </p>
          ) : isLoading && data === null ? (
            <p className="muted" style={{ fontSize: 13 }}>
              Loading…
            </p>
          ) : jobs.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>
              You’re not on any jobs yet, so there is nothing to compare.
            </p>
          ) : (
            <>
              {failedRefresh && (
                <p className="muted" style={{ fontSize: 12.5 }} role="status">
                  Couldn’t refresh — showing the last loaded rates.
                </p>
              )}
              {jobs.length === 1 && (
                <p className="muted" style={{ fontSize: 12.5 }}>
                  This is the only job you’re on — the column below is its own roster.
                </p>
              )}
              {shownJobs.length === 0 ? (
                // The picker above is still on screen, so this is recoverable
                // by the control that caused it.
                <p className="muted" style={{ fontSize: 13 }}>
                  Tick at least one job.
                </p>
              ) : visible.length === 0 ? (
                <p className="muted" style={{ fontSize: 13 }}>
                  {/* Counted over the TICKED jobs, like the line above it:
                      with one ticked job whose roster is empty, the
                      response-wide count is non-zero and this would blame a
                      search nobody typed. */}
                  {emptyMessage({
                    kind,
                    rowCount: carried.length,
                    jobsNarrowed: shownJobs.length < jobs.length,
                    onlyDifferences: differencesOnly,
                    searching: query.trim() !== '',
                  })}
                </p>
              ) : (
                <>
                  {/* How much of the roster is on screen, stated once
                      (RULES.md 1.5) — this table has no section title to
                      carry it. Not rendered over an empty table: the empty
                      message is that table's one line. */}
                  <p className="muted" style={countsStyle}>
                    {countsLine({
                      total: carried.length,
                      shown: visible.length,
                      query,
                      onlyDifferences: differencesOnly,
                      kind,
                    })}
                  </p>
                  {/* The frame exists to position the overflow cue OVER the
                      scroller, including over the sticky header band — which
                      is exactly the band an inset shadow on the scroller
                      could not reach, since the header cells are opaque. */}
                  <div style={tableFrameStyle}>
                    <div
                      ref={scrollRef}
                      className="table-scroll table-scroll--sticky-key"
                      style={tableScrollStyle}
                      onScroll={measureOverflow}
                    >
                      <table
                        className="table"
                        // A FLOOR at the real columns' own width, with
                        // `.table`'s width: 100% doing the filling: under
                        // that floor the frame scrolls sideways, over it the
                        // filler column takes the slack (see below). It has
                        // to be inline — `.table-scroll`'s own 860px floor is
                        // a page-table number, and the opt-in that zeroes it
                        // would zero this one too.
                        style={{ minWidth: columnsWidth(shownJobs.length) }}
                      >
                        <thead>
                          <tr>
                            {/* Pinned left: with a money column per job the table
                                scrolls sideways, and a rate row whose name has
                                scrolled off is unreadable. The class sets
                                table-layout: fixed and every money column a width,
                                so this one absorbs the slack. */}
                            <th className="sticky-key" style={keyColStyle}>
                              {isEmployees ? 'Employee' : 'Asset'}
                            </th>
                            {shownJobs.map((job) => (
                              // Number only. The name line under it pushed eight
                              // columns past the right edge of the modal, so the
                              // name moved to the app's own tooltip — the native
                              // `title` it had before was slow enough to arrive
                              // that nobody found it. No bubble title: the number
                              // is already under the cursor (RULES.md 1.5).
                              <th key={job.project_id} className="num" style={moneyColStyle}>
                                <HelpTip label={job.project_number}>{job.name}</HelpTip>
                              </th>
                            ))}
                            {/* FILLER. The money columns are 96px and the
                                name column 240, so with a few jobs ticked
                                the real columns do not reach the right edge
                                — and a bare white gap beside a styled table
                                reads as a rendering fault (client ruling
                                2026-09-21). This column has no width, so
                                table-layout: fixed hands it everything left
                                over: the header band and every row rule run
                                edge to edge while nothing that carries a
                                number changes size. It collapses to nothing
                                the moment the real columns fill the frame.
                                Empty and aria-hidden — there is no column
                                here, only the space one would occupy. */}
                            <th aria-hidden />
                          </tr>
                        </thead>
                        <tbody>
                          {visible.map((row) => {
                            const rates = ratesFor(row);
                          const [lead, under] = keyCellLines(row, kind);
                            return (
                              <tr key={row.key}>
                                <td className="sticky-key">
                                  {/* Which line leads is keyCellLines'
                                      decision and nothing else's — employees
                                      by name, equipment by asset id (client
                                      rulings 2026-09-22). Here we only dress
                                      the rank we were handed: the lead in the
                                      table's own cell voice, the line under
                                      it small and muted, and mono on whichever
                                      of the two is the identifier (RULES.md
                                      5.1).
                                      Both lines clip: the column is a fixed
                                      240px, and a wrapped name would make one
                                      row twice the height of its neighbours
                                      for the sake of a surname. */}
                                  <div
                                    className={lead.voice === 'id' ? 'mono' : undefined}
                                    style={
                                      lead.voice === 'name' ? rowLeadNameStyle : rowLeadIdStyle
                                    }
                                  >
                                    {lead.text}
                                  </div>
                                  <div
                                    className={under.voice === 'id' ? 'mono' : undefined}
                                    style={rowUnderStyle}
                                  >
                                    {under.text}
                                  </div>
                                </td>
                                {shownJobs.map((job) => (
                                  <td key={job.project_id} className="num">
                                    {formatCell(rates[job.project_id] ?? null)}
                                  </td>
                                ))}
                                {/* The filler's own cell — see the header. */}
                                <td aria-hidden />
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* The one thing on screen that says a column is out
                        there. Inset by the measured gutters so the shade
                        never lands on a scrollbar track. */}
                    {overflow.moreRight && (
                      <span
                        className="table-more-right"
                        style={{ right: overflow.gutterRight, bottom: overflow.gutterBottom }}
                        aria-hidden
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Gap between trigger and menu, and the viewport margin the menu keeps. */
const MENU_GAP = 4;
const MENU_EDGE = 8;
const MENU_MAX_H = 320;
const MENU_MIN_H = 120;
const MENU_MIN_W = 250;
const MENU_MAX_W = 420;

interface MenuPos {
  left: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

const samePos = (a: MenuPos, b: MenuPos) =>
  a.left === b.left && a.top === b.top && a.bottom === b.bottom && a.maxHeight === b.maxHeight;

/**
 * Which jobs are columns — one quiet button and a list of checkboxes behind it.
 *
 * It was a row of eight numbered checkboxes standing open above the table.
 * That row cost a line of chrome permanently, and the numbers were all it had
 * room for, so a PM who knows the work and not the numbering had nowhere to
 * learn which job `26105` is. Folded into a popover, each row carries the
 * number AND the name, and the closed button states what the table is showing
 * in four words.
 *
 * Portal to <body> with `position: fixed`, the same as Dropdown / ActionMenu /
 * HelpTip and for the same reason: this opens inside `.new-pco-modal`, which
 * carries `overflow: hidden` for its radius, and an in-flow menu would be
 * clipped by it with no z-index able to help. The popover chrome itself is
 * `.dropdown-menu` / `.dropdown-opt`, unchanged — it is the app's one popover
 * vocabulary, and `.dropdown-menu`'s z-index is already set to clear modals.
 *
 * Checkboxes, not a listbox: every row is independently on or off, and the
 * native control is what says so to a screen reader and what Tab reaches.
 */
function JobsFilter({
  jobs,
  hidden,
  shownCount,
  onToggle,
  onSelectAll,
}: {
  jobs: ReadonlyArray<RateComparisonJob>;
  hidden: ReadonlySet<string>;
  shownCount: number;
  onToggle: (projectId: string, shown: boolean) => void;
  onSelectAll: () => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos>({ left: 0, maxHeight: MENU_MAX_H });

  // One function for open, scroll and resize. On the first pass the menu is
  // not in the DOM yet, so it places below off the trigger's own box; the
  // layout effect runs it again with a real measurement and flips it up only
  // if the real box would clip.
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const menu = menuRef.current;
    const spaceBelow = window.innerHeight - r.bottom - MENU_GAP - MENU_EDGE;
    const spaceAbove = r.top - MENU_GAP - MENU_EDGE;
    const wanted = menu ? Math.min(menu.scrollHeight, MENU_MAX_H) : 0;
    const up = wanted > spaceBelow && spaceAbove > spaceBelow;
    const maxHeight = Math.max(MENU_MIN_H, Math.min(MENU_MAX_H, up ? spaceAbove : spaceBelow));
    const w = menu ? menu.getBoundingClientRect().width : MENU_MIN_W;
    // Left edges meet — the button sits mid-row, so the list hangs under it.
    const left = Math.max(MENU_EDGE, Math.min(r.left, window.innerWidth - MENU_EDGE - w));
    const next: MenuPos = {
      left,
      maxHeight,
      top: up ? undefined : r.bottom + MENU_GAP,
      bottom: up ? window.innerHeight - r.top + MENU_GAP : undefined,
    };
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, []);

  // Measure-then-correct, before paint — the clipped frame is never shown.
  useLayoutEffect(() => {
    if (open) place();
  });

  useEffect(() => {
    if (!open) return;
    // Capture phase: the settings pane behind this scrolls internally, and a
    // bubbling scroll listener on window never hears those.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // Outside click closes. mousedown, not click, so the menu is gone before the
  // click lands on whatever was underneath it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Tab out of the trigger lands wherever the modal's own order says, because
  // the list is portaled to the end of <body> — so opening moves focus into
  // it, and Escape (below) hands it back. Without this the checkboxes are
  // only reachable after tabbing through the rest of the window.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('button, input')?.focus();
  }, [open]);

  // Escape closes the LIST, not the window. Capture phase on the document, so
  // it runs — and stops — before the modal's own window-level Escape handler
  // can take the whole screen away from under an open popover.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  const allShown = shownCount === jobs.length;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // Ghost rank (RULES.md 2.1/2.2): this narrows what you are reading, it
        // does not do anything, so it never competes with a filled button.
        className="btn btn-ghost"
        // No aria-haspopup: what opens is a group of checkboxes, neither a
        // menu nor a dialog, and either value would promise keyboard
        // behaviour this does not have. aria-expanded carries the state.
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {jobsFilterLabel({ shown: shownCount, total: jobs.length })}
        <ChevronDown size={14} strokeWidth={2} aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="dropdown-menu"
            role="group"
            aria-label="Jobs to compare"
            style={{
              position: 'fixed',
              left: pos.left,
              ...(pos.top !== undefined ? { top: pos.top } : {}),
              ...(pos.bottom !== undefined ? { bottom: pos.bottom } : {}),
              minWidth: MENU_MIN_W,
              maxWidth: Math.min(MENU_MAX_W, window.innerWidth - MENU_EDGE * 2),
              maxHeight: pos.maxHeight,
            }}
          >
            {/* Only while something is off — "Select all" over a full list is
                a control with nothing to do (RULES.md 7.7). Quiet link rank
                (RULES.md 2.4): it sits above the rows, it is not one of them. */}
            {!allShown && (
              <button
                type="button"
                className="btn btn-link"
                style={selectAllStyle}
                onClick={() => {
                  onSelectAll();
                  // This button unmounts the instant it works — there is
                  // nothing left to select — so focus has to be handed
                  // somewhere on purpose: the list it just refilled, not the
                  // document. Same reason as the search box's own clear.
                  menuRef.current?.querySelector<HTMLInputElement>('input')?.focus();
                }}
              >
                Select all
              </button>
            )}
            {jobs.map((job) => (
              <label key={job.project_id} className="dropdown-opt" style={jobOptStyle}>
                <input
                  type="checkbox"
                  checked={!hidden.has(job.project_id)}
                  onChange={(e) => onToggle(job.project_id, e.target.checked)}
                />
                <span className="mono" style={jobOptNumberStyle}>
                  {job.project_number}
                </span>
                {/* One line, clipped: this is where a PM reads which number is
                    which, and a two-line name would turn the list into a wall. */}
                <span style={jobOptNameStyle}>{job.name}</span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

const bodyStyle: CSSProperties = {
  padding: '6px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flex: 1,
  minHeight: 0,
};

const controlsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  flexWrap: 'wrap',
};

// Relative, so the ✕ can sit inside the box's right edge.
const searchWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
};

const searchStyle: CSSProperties = {
  // Right padding clears the ✕ — text running under it reads as a typo.
  padding: '6px 26px 6px 8px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
  width: 260,
};

const toggleLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: 'var(--ink-2)',
  cursor: 'pointer',
};

// The table owns the only scrollbar in the modal: flex into the space under
// the controls rather than the page-sized caps the class carries by default.
const tableScrollStyle: CSSProperties = { flex: 1, minHeight: 0, maxHeight: 'none' };

// Positioning context for the overflow cue, and otherwise invisible: it takes
// the flex slot the scroller used to hold and passes it straight down.
const tableFrameStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
};

// One line each, clipped — see KEY_COL_PX for what fits in the 240px column
// and what the ellipsis is there for.
const clipStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// A NAME on the lead line takes the table's ordinary cell voice plus the
// weight `.table td.name` gives a name beside an id (index.css:1192) — the
// same pairing the roster lists under this modal use.
const rowLeadNameStyle: CSSProperties = {
  ...clipStyle,
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--ink)',
};

// An ID on the lead line states nothing and inherits everything: 13px full
// ink from `.table`, with the mono coming off the className. This is the
// equipment side exactly as it has always drawn — the ruling that promoted
// the employee name deliberately left it alone.
const rowLeadIdStyle: CSSProperties = { ...clipStyle };

// The line underneath, whichever of the two it is: small and muted, and mono
// only when it is the identifier.
const rowUnderStyle: CSSProperties = {
  ...clipStyle,
  fontSize: 12,
  color: 'var(--ink-3)',
  marginTop: 2,
};

const countsStyle: CSSProperties = { fontSize: 12.5, margin: 0 };

// Inset to the option rows' own 8px so it reads as a line above the list
// rather than a row in it. Height auto: .btn's 32px is a control's height, and
// this is a link.
const selectAllStyle: CSSProperties = {
  fontSize: 12.5,
  height: 'auto',
  margin: '2px 8px 6px',
};

const jobOptStyle: CSSProperties = { cursor: 'pointer' };

const jobOptNumberStyle: CSSProperties = { fontSize: 12.5, flexShrink: 0 };

const jobOptNameStyle: CSSProperties = {
  fontSize: 12.5,
  color: 'var(--ink-3)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Every column that carries something has a fixed width and keeps it: a
 *  rate under a column that changes size as you tick jobs is a rate you have
 *  to find again (client ruling 2026-09-21). What absorbs the difference is
 *  the trailing filler column, which carries nothing at all. */
const MONEY_COL_PX = 96;
/** Fixed, not a minimum — it never absorbs slack. 240 holds both lines for
 *  every row in the roster today, measured against the cell's 212px of
 *  content box: the longest employee name in the roster today is
 *  156px in the 13px/500 lead voice it took on 2026-09-22, and the longest
 *  equipment description, "2024 Chevy Silverado 3500HD 4x4", is 187px in the
 *  12px voice it keeps under the asset id. Anything longer clips with an
 *  ellipsis rather than wrapping. */
const KEY_COL_PX = 240;
/** What the columns that carry something actually measure. It is the table's
 *  min-width (below it the frame scrolls) and the number the window is sized
 *  from. */
const columnsWidth = (columns: number) => KEY_COL_PX + columns * MONEY_COL_PX;

const keyColStyle: CSSProperties = { width: KEY_COL_PX };

const moneyColStyle: CSSProperties = { width: MONEY_COL_PX };

/** The modal's own chrome, between its outer edge and the scroller's content
 *  box: the body's 20px padding either side, the modal's two 1px borders, and
 *  an allowance for the vertical scrollbar a long roster always puts in the
 *  gutter. 18px rather than the 15 Chrome actually uses, so the eighth column
 *  is not clipped wherever the bars are wider (17 on some Windows setups);
 *  any pixels left over go to the filler column, not to a gap. */
const MODAL_CHROME_PX = 40 + 2 + 18;
/** The window is built for eight money columns, and that is a CONSTANT, not
 *  a count of anybody's jobs: Coffman will hold more than eight before long
 *  (Brandon 2026-09-21), and a window whose size depends on how many would
 *  then differ per reader and change under the one who gets the ninth. Past
 *  eight the table simply scrolls sideways, which it is built to do. */
const MODAL_COLUMNS = 8;
/** One width, every open: eight money columns, the name column, and the
 *  chrome around them — capped at 96vw so the modal never outgrows the
 *  viewport it opens in. */
const MODAL_MAX_WIDTH = `min(${columnsWidth(MODAL_COLUMNS) + MODAL_CHROME_PX}px, 96vw)`;

/** What the scroller's box says about the columns off its right edge, and
 *  about the space its scrollbars are taking. One object, so the cue can
 *  never be drawn from one render's overflow and another's gutters. */
interface Overflow {
  moreRight: boolean;
  gutterRight: number;
  gutterBottom: number;
}

const NO_OVERFLOW: Overflow = { moreRight: false, gutterRight: 0, gutterBottom: 0 };

const sameOverflow = (a: Overflow, b: Overflow) =>
  a.moreRight === b.moreRight &&
  a.gutterRight === b.gutterRight &&
  a.gutterBottom === b.gutterBottom;
